import { Mic } from 'lucide-react';
import type { UserMemory } from '../../shared/memory.ts';
import { latestResult } from '../../shared/rounds.ts';
import { CEFR_LEVELS } from '../../shared/scoring.ts';
import { grammarFocus, MIN_LEVEL_SAMPLES, overallCefr, weakPhonemes } from '../../shared/skills.ts';
import { cn } from '../lib/cn.ts';
import { useMemory } from '../shell/useMemory.tsx';

// Grouped the way the Stimuler app groups its phoneme tiles (backend phoneme_proficiency_models.py).
export const PHONEME_GROUPS: Array<{ group: string; sets: Array<{ name: string; phonemes: string[] }> }> = [
  {
    group: 'Vowels',
    sets: [
      { name: 'Front', phonemes: ['i', 'ɪ', 'ɛ', 'æ'] },
      { name: 'Central', phonemes: ['ə', 'ʌ', 'ɝ'] },
      { name: 'Back', phonemes: ['u', 'ʊ', 'ɔ', 'ɑ'] },
      { name: 'Diphthongs', phonemes: ['aɪ', 'aʊ', 'eɪ', 'oʊ', 'ɔɪ'] },
    ],
  },
  {
    group: 'Consonants',
    sets: [
      { name: 'Stops', phonemes: ['p', 'b', 't', 'd', 'k', 'ɡ'] },
      { name: 'Fricatives', phonemes: ['f', 'v', 'θ', 'ð', 's', 'z', 'ʃ', 'ʒ', 'h'] },
      { name: 'Affricates', phonemes: ['tʃ', 'dʒ'] },
      { name: 'Nasals', phonemes: ['m', 'n', 'ŋ'] },
      { name: 'Approximants', phonemes: ['l', 'r', 'w', 'j'] },
    ],
  },
];

// The 8 learner-facing grammar categories (ml-infra vocab_grammar validation.py).
const GRAMMAR_CATEGORIES = [
  'Verbs & Tenses',
  'Subject-Verb Agreement',
  'Articles & Quantifiers',
  'Prepositions',
  'Nouns & Pronouns',
  'Adjectives & Adverbs',
  'Sentence Structure',
  'Vocabulary & Word Choice',
];

// Stimuler's bands: good above 70, neutral 50-70, needs work below 50.
// Banded on the rounded value the tile shows, so a "50" is never red.
const band = (score: number) => {
  const shown = Math.round(score);
  return shown > 70 ? 'good' : shown >= 50 ? 'neutral' : 'bad';
};
const BAND_TILE = {
  good: 'bg-good/10 text-good ring-good/25',
  neutral: 'bg-warn/10 text-warn ring-warn/25',
  bad: 'bg-bad/10 text-bad ring-bad/25',
} as const;
const BAND_TEXT = { good: 'text-good', neutral: 'text-warn', bad: 'text-bad' } as const;

function Section({ title, children, testId }: { title: string; children: React.ReactNode; testId?: string }) {
  return (
    <section data-testid={testId} className="card space-y-3 ring-1 ring-fg/10">
      <h2 className="text-xs font-medium uppercase tracking-wide text-subtle">{title}</h2>
      {children}
    </section>
  );
}

const CHART = PHONEME_GROUPS.flatMap((g) => g.sets.flatMap((x) => x.phonemes));

// A word with each sound, for the tiles that lead the page: most candidates don't read IPA.
export const EXAMPLE: Record<string, string> = {
  i: 'see',
  ɪ: 'sit',
  ɛ: 'bed',
  æ: 'cat',
  ə: 'about',
  ʌ: 'cup',
  ɝ: 'bird',
  u: 'food',
  ʊ: 'book',
  ɔ: 'law',
  ɑ: 'father',
  aɪ: 'time',
  aʊ: 'now',
  eɪ: 'day',
  oʊ: 'home',
  ɔɪ: 'boy',
  p: 'pen',
  b: 'bad',
  t: 'ten',
  d: 'dog',
  k: 'kite',
  ɡ: 'go',
  f: 'fan',
  v: 'very',
  θ: 'think',
  ð: 'this',
  s: 'sun',
  z: 'zoo',
  ʃ: 'she',
  ʒ: 'vision',
  h: 'hat',
  tʃ: 'chair',
  dʒ: 'job',
  m: 'man',
  n: 'no',
  ŋ: 'sing',
  l: 'leg',
  r: 'red',
  w: 'we',
  j: 'yes',
};

const TO_LEVEL: Record<number, string> = { 1: 'One more answer', 2: 'Two more answers' };
export const day = (iso: string, now = new Date()) => {
  const d = new Date(iso);
  const thisYear = d.getFullYear() === now.getFullYear();
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', ...(thisYear ? {} : { year: 'numeric' }) });
};
// Early scores move a point or two either way from answer to answer; smaller changes are noise, not progress.
const MIN_CHANGE = 3;

type Baseline = NonNullable<UserMemory['assessment']['baseline']>;

/**
 * The line that says where they started, or null when the baseline holds nothing to compare against. A level only
 * counts as risen between two real CEFR levels; anything else stored is shown as it is.
 */
export function startLine(start: Baseline | undefined, level: string | null, now = new Date()): string | null {
  if (!start) return null;
  const when = `(${day(start.at, now)})`;
  if (start.cefr) {
    const known = (l: string | null): l is (typeof CEFR_LEVELS)[number] => !!l && CEFR_LEVELS.includes(l as never);
    const rose = known(start.cefr) && known(level) && CEFR_LEVELS.indexOf(level) > CEFR_LEVELS.indexOf(start.cefr);
    return `${rose ? 'Up from' : 'Started at'} ${start.cefr} ${when}`;
  }
  if (start.overall !== null) return `Started at ${Math.round(start.overall)}/100 ${when}`;
  const metrics = [start.pronunciation, start.fluency, start.grammar, start.vocabulary];
  // No level or score to name, and right after the move nothing has changed yet: just when.
  return metrics.some((m) => m !== null) ? `Started on ${day(start.at, now)}` : null;
}

/** The line under it for the latest check-in (flow 002) with a result: its level, or its score, and when. */
export function checkInLine(result: Baseline | undefined, now = new Date()): string | null {
  if (!result) return null;
  if (result.cefr) return `${result.cefr} at your check-in (${day(result.at, now)})`;
  if (result.overall !== null) return `${Math.round(result.overall)}/100 at your check-in (${day(result.at, now)})`;
  return null;
}

/** The change since the baseline, on the rounded values the tiles show; null when there's none worth showing. */
export function changeSince(now: number | null, from: number | null | undefined): number | null {
  if (now === null || from === null || from === undefined) return null;
  const diff = Math.round(now) - Math.round(from);
  return Math.abs(diff) < MIN_CHANGE ? null : diff;
}

/**
 * The change since the baseline, on the rounded values the tiles show. A drop is muted, not red: with few answers it
 * is as likely the mix of tasks as a real slip, and a red number reads as failing a warm-up.
 */
function Change({ now, from }: { now: number | null; from: number | null | undefined }) {
  const diff = changeSince(now, from);
  if (diff === null) return null;
  return (
    <span
      data-testid="progress-change"
      className={cn('ml-1.5 text-xs font-medium', diff > 0 ? 'text-good' : 'text-subtle')}
    >
      {diff > 0 ? `+${diff}` : `−${-diff}`}
      <span className="sr-only"> since you started</span>
    </span>
  );
}

function PractiseButton({ onPractise }: { onPractise: () => void }) {
  return (
    <button type="button" data-testid="progress-practise" onClick={onPractise} className="btn-primary">
      <Mic className="size-4" /> Practise a question on a call
    </button>
  );
}

export function ProgressTab({ onPractise, soundsOpen }: { onPractise: () => void; soundsOpen?: boolean }) {
  const { memory } = useMemory();
  const s = memory.skills;
  if (!s.samples) {
    return (
      <div data-testid="progress-tab" className="mx-auto max-w-2xl space-y-4 px-4 pb-28 pt-10">
        <h1 className="text-2xl font-semibold tracking-tight text-fg">Your progress</h1>
        <p className="text-subtle">Answer a question on a call and your progress shows up here.</p>
        <PractiseButton onPractise={onPractise} />
      </div>
    );
  }
  const weakSounds = weakPhonemes(s);
  const focus = grammarFocus(s);
  // Only categories that were corrected, the 8 learner categories first in their usual order, then any other label.
  const corrected = [
    ...GRAMMAR_CATEGORIES,
    ...Object.keys(s.grammarErrors).filter((c) => !GRAMMAR_CATEGORIES.includes(c)),
  ].flatMap((c) => ((s.grammarErrors[c] ?? 0) > 0 ? [[c, s.grammarErrors[c] ?? 0] as const] : []));
  const others = corrected.filter(([c]) => !focus.includes(c));
  const level = overallCefr(s);
  const start = memory.assessment.baseline;
  const metrics = [
    ['Pronunciation', s.pronunciation, start?.pronunciation],
    ['Fluency', s.fluency, start?.fluency],
    ['Grammar', s.grammar, start?.grammar],
    ['Vocabulary', s.vocabulary, start?.vocabulary],
  ] as const;
  const answersToLevel = MIN_LEVEL_SAMPLES - s.cefrHistory.length;
  // Changes only make sense next to the line that says where they're measured from.
  const started = startLine(start, level);
  const checkedIn = checkInLine(latestResult(memory));
  const vocabTotal = Object.values(s.vocabCefr).reduce((a, b) => a + b, 0);
  const pace = s.wpm === null ? null : Math.round(s.wpm);

  return (
    <div data-testid="progress-tab" className="mx-auto w-full max-w-2xl space-y-5 px-4 pb-28 pt-10">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight text-fg">Your progress</h1>
        <p className="text-sm text-subtle">
          From {s.samples} scored answer{s.samples === 1 ? '' : 's'}. Recent answers count most.
        </p>
      </header>

      <Section title="Level" testId="progress-level">
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className={cn('text-4xl font-semibold', s.overall === null ? 'text-fg' : BAND_TEXT[band(s.overall)])}>
              {s.overall === null ? '–' : Math.round(s.overall)}
              <span className="text-base font-normal text-subtle">/100</span>
            </p>
          </div>
          {level ? (
            <span
              data-testid="progress-cefr"
              title="CEFR level"
              className="rounded-full bg-accent/10 px-4 py-1.5 text-lg font-semibold text-accent ring-1 ring-accent/30"
            >
              {level}
            </span>
          ) : null}
        </div>
        {!level && answersToLevel > 0 ? (
          <p data-testid="progress-level-pending" className="text-sm text-subtle">
            {TO_LEVEL[answersToLevel] ?? 'A few more answers'} in your own words and I'll place you on the A1–C2 scale.
          </p>
        ) : null}
        {started ? (
          <p data-testid="progress-baseline" className="text-sm text-subtle">
            {started}
          </p>
        ) : null}
        {checkedIn ? (
          <p data-testid="progress-checkin" className="text-sm text-subtle">
            {checkedIn}
          </p>
        ) : null}
        <dl className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {metrics.map(([label, value, from]) => (
            <div key={label} className="rounded-xl bg-fg/[0.03] px-3 py-2 ring-1 ring-fg/5">
              <dt className="text-xs text-subtle">{label}</dt>
              <dd className={cn('text-lg font-semibold', value === null ? 'text-subtle' : BAND_TEXT[band(value)])}>
                {value === null ? '–' : Math.round(value)}
                {started ? <Change now={value} from={from} /> : null}
              </dd>
            </div>
          ))}
        </dl>
      </Section>

      {/* First after the level: what the coach will work on, so the page opens on the next step. */}
      <Section title="Practise these" testId="progress-practise-these">
        {weakSounds.length || focus.length ? (
          <div className="space-y-3">
            {weakSounds.length ? (
              <div className="flex flex-wrap gap-1.5">
                {weakSounds.map((p) => (
                  <span
                    data-testid={`practise-sound-${p.phoneme}`}
                    key={p.phoneme}
                    className={cn(
                      'inline-flex min-w-11 flex-col items-center rounded-lg px-2 py-1 text-sm ring-1',
                      BAND_TILE[band(p.score)],
                    )}
                  >
                    /{p.phoneme}/<span className="text-[10px]">{p.score}</span>
                    {EXAMPLE[p.phoneme] ? <span className="text-[11px] text-muted">{EXAMPLE[p.phoneme]}</span> : null}
                  </span>
                ))}
              </div>
            ) : null}
            {focus.map((c) => (
              <p key={c} className="text-[15px] text-fg/85">
                {c}{' '}
                <span className="text-subtle">
                  ({s.grammarErrors[c]} correction{s.grammarErrors[c] === 1 ? '' : 's'})
                </span>
              </p>
            ))}
          </div>
        ) : (
          <p className="text-sm text-subtle">Nothing stands out yet. A few more answers and I'll point to it.</p>
        )}
        <details data-testid="progress-sounds" className="space-y-3 border-t border-fg/10 pt-3" open={soundsOpen}>
          <summary className="-mt-2 mb-0 cursor-pointer select-none py-3 text-sm text-muted">
            All {CHART.length} sounds, heard {CHART.filter((p) => s.phonemes[p]).length} so far
          </summary>
          {PHONEME_GROUPS.map(({ group, sets }) => (
            <div key={group} className="space-y-2">
              <h3 className="text-sm text-muted">{group}</h3>
              {sets.map((set) => (
                // Label above the tiles on phones (long rows wrap cleanly), beside them on wider screens.
                <div key={set.name} className="flex flex-col gap-1.5 sm:flex-row sm:items-start">
                  <span className="shrink-0 text-xs text-subtle sm:w-24 sm:pt-2">{set.name}</span>
                  <div className="flex flex-wrap gap-1.5">
                    {set.phonemes.map((p) => {
                      const stat = s.phonemes[p];
                      return (
                        <span
                          key={p}
                          data-testid={`phoneme-${p}`}
                          data-band={stat ? band(stat.ema) : 'unheard'}
                          title={
                            stat ? `/${p}/ ${Math.round(stat.ema)} (heard ${stat.count}×)` : `/${p}/ not heard yet`
                          }
                          className={cn(
                            'inline-flex min-w-11 flex-col items-center rounded-lg px-2 py-1 text-sm ring-1',
                            stat ? BAND_TILE[band(stat.ema)] : 'text-subtle ring-fg/10',
                          )}
                        >
                          /{p}/<span className="text-[10px]">{stat ? Math.round(stat.ema) : '·'}</span>
                        </span>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          ))}
          <p className="text-xs text-subtle">Green above 70 · amber 50–70 · red below 50 · grey not heard yet</p>
        </details>
      </Section>
      <PractiseButton onPractise={onPractise} />

      <Section title="Pace and flow" testId="progress-pace">
        {pace === null ? (
          <p className="text-sm text-subtle">Not measured yet.</p>
        ) : (
          <div className="space-y-2">
            <p className="text-[15px] text-fg/85">
              <span className="text-2xl font-semibold">{pace}</span> words a minute{' '}
              <span className="text-subtle">
                {pace < 125 ? '(a little slow)' : pace > 175 ? '(a little fast)' : '(clear, in the 125–175 range)'}
              </span>
            </p>
            {/* 50-250 wpm scale with the 125-175 comfort band shaded. */}
            <div className="relative h-2 rounded-full bg-fg/10">
              <div className="absolute inset-y-0 rounded-full bg-good/30" style={{ left: '37.5%', width: '25%' }} />
              <div
                className="absolute -top-1 h-4 w-1 rounded bg-fg"
                style={{ left: `${Math.min(100, Math.max(0, ((pace - 50) / 200) * 100))}%` }}
              />
            </div>
          </div>
        )}
        <p className="text-sm text-subtle">
          {s.fillers === null ? '' : `About ${Math.round(s.fillers * 10) / 10} fillers (um, uh, like) per answer. `}
          {s.pauses === null ? '' : `About ${Math.round(s.pauses * 10) / 10} long pauses per answer.`}
        </p>
      </Section>

      {/* "Practise these" already names the main patterns; this lists the rest, so nothing is said twice. */}
      {focus.length && !others.length ? null : (
        <Section title={focus.length ? 'Other grammar corrections' : 'Grammar corrections'} testId="progress-grammar">
          {others.length ? (
            <ul className="space-y-1.5">
              {others.map(([c, n]) => (
                <li key={c} className="flex items-center justify-between text-[15px]">
                  <span className="text-fg/85">{c}</span>
                  {/* One correction is a slip, not a pattern (grammarFocus): not in the warn tone. */}
                  <span className={n >= 2 ? 'font-semibold text-warn' : 'text-muted'}>{n}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-subtle">
              {/* Read-aloud answers carry no grammar, so none measured is not the same as none corrected. */}
              {s.grammar === null ? 'Not measured yet.' : 'No grammar corrections yet.'}
            </p>
          )}
        </Section>
      )}

      {vocabTotal ? (
        <Section title="Vocabulary level" testId="progress-vocab">
          <div className="flex h-3 overflow-hidden rounded-full">
            {CEFR_LEVELS.map((l, i) => {
              const n = s.vocabCefr[l] ?? 0;
              return n ? (
                <div
                  key={l}
                  title={`${l}: ${n} words`}
                  className="h-full"
                  style={{ width: `${(n / vocabTotal) * 100}%`, background: `hsl(${200 - i * 25} 80% ${70 - i * 6}%)` }}
                />
              ) : null;
            })}
          </div>
          <p className="text-xs text-subtle">
            {CEFR_LEVELS.map((l) => `${l} ${Math.round(((s.vocabCefr[l] ?? 0) / vocabTotal) * 100)}%`).join(' · ')}
          </p>
        </Section>
      ) : null}

      {memory.savedWords.length ? (
        <Section title="Saved words">
          <div className="flex flex-wrap gap-2">
            {memory.savedWords.map((w) => (
              <span key={w.word} title={w.meaning} className="rounded-full bg-fg/5 px-3 py-1 text-sm text-fg/80">
                {w.word}
              </span>
            ))}
          </div>
        </Section>
      ) : null}
    </div>
  );
}
